package link.erpango.quip.core.interactor

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.launch
import link.erpango.quip.data.IDataSource
import link.erpango.quip.data.remote.DResponse
import link.erpango.quip.domain.entities.DMaintenance
import link.erpango.quip.domain.entities.DSparePart
import link.erpango.quip.utils.FileUtils
import link.erpango.quip.domain.entities.DVehicle
import link.erpango.quip.domain.entities.DVehicleMaintenance
import link.erpango.quip.domain.entities.DVehicleDocument
import link.erpango.quip.domain.entities.DSparePartDocument
import link.erpango.quip.domain.entities.VehicleType
import link.erpango.quip.domain.entities.AcceptTransferResponse
import link.erpango.quip.domain.entities.CancelTransferResponse
import link.erpango.quip.domain.entities.PendingTransferItem
import link.erpango.quip.domain.entities.TransferQRResponse
import link.erpango.quip.domain.usecases.GarageErrors
import link.erpango.quip.domain.usecases.IGarageUseCase
import link.erpango.quip.utils.DatetimeUtils
import link.erpango.quip.utils.PlatformUtils
import linkerpangoquipdb.Maintenance
import kotlin.coroutines.CoroutineContext

class GarageInteractor(
    override val data: IDataSource
): IGarageUseCase ,IInteractor {
    override suspend fun getFullGarage(): List<DVehicle> {
        return data.db.garage.selectAllVehicles()
    }

    override fun getGarage(scope: CoroutineContext): Flow<List<DVehicle>> {
        return data.db.garage.observeVehicles(scope)
    }

    override suspend fun createVehicle(vehicle: DVehicle): DResponse<DVehicle> {
        if (data.db.garage.selectVehicle(vehicle.vin) != null) {
            FileUtils.log("createVehicle: Vehicle ${vehicle.vin} already exists locally, skipping creation")
            return DResponse.Error(GarageErrors.DUPLICATE_LOCAL_VIN)
        }
        val response = data.remote.garage.createVehicle(vehicle.copy(maintenances = emptyList()))
        if (response is DResponse.Success) {
            val created = response.data
            data.db.garage.insertVehicle(
                vin = created.vin,
                plate = created.plate,
                brand = created.brand,
                model = created.model,
                vehicleType = created.vehicleType.code,
                year = created.year.toLong(),
                color = created.color,
                owner = created.owner,
                engineOil = created.engineOil,
                frontTireSize = created.frontTireSize,
                rearTireSize = created.rearTireSize,
                frontTirePressureBelow160 = created.frontTirePressureBelow160,
                rearTirePressureBelow160 = created.rearTirePressureBelow160,
                frontTirePressureAbove160 = created.frontTirePressureAbove160,
                rearTirePressureAbove160 = created.rearTirePressureAbove160,
                recommendedFuel = created.recommendedFuel,
                engineDetails = created.engineDetails,
                cylinderCount = created.cylinderCount,
                frontBulbType = created.frontBulbType,
                rearBulbType = created.rearBulbType,
                batterySpecs = created.batterySpecs,
                coolantType = created.coolantType,
                signature = created.signature,
                nextRevisionDate = created.nextRevisionDate,
                nextItvDate = created.nextItvDate,
                firstRegistrationDate = created.firstRegistrationDate
            )
            try {
                data.firebase.analytics.logEvent("vehicle_added", vehicle.vin)
            } catch (e: Exception) {
                e.printStackTrace()
            }
        } else {
            val error = response as? DResponse.Error
            FileUtils.log("createVehicle: Error adding vehicle ${vehicle.vin}: ${error?.error}")
        }
        return response
    }

    override suspend fun createVehicle(
        vin: String,
        plate: String,
        brand: String,
        model: String,
        vehicleType: VehicleType,
        year: Long,
        color: String,
        owner: String,
        engineOil: String,
        frontTireSize: String,
        rearTireSize: String,
        frontTirePressureBelow160: Double,
        rearTirePressureBelow160: Double,
        frontTirePressureAbove160: Double,
        rearTirePressureAbove160: Double,
        recommendedFuel: String,
        engineDetails: String,
        cylinderCount: Int,
        signature: String?,
        nextRevisionDate: Long?,
        nextItvDate: Long?,
        firstRegistrationDate: Long?
    ) {
        createVehicle(
            DVehicle(
                vin = vin,
                plate = plate,
                brand = brand,
                model = model,
                vehicleType = vehicleType,
                year = year.toInt(),
                color = color,
                owner = owner,
                engineOil = engineOil,
                frontTireSize = frontTireSize,
                rearTireSize = rearTireSize,
                frontTirePressureBelow160 = frontTirePressureBelow160,
                rearTirePressureBelow160 = rearTirePressureBelow160,
                frontTirePressureAbove160 = frontTirePressureAbove160,
                rearTirePressureAbove160 = rearTirePressureAbove160,
                recommendedFuel = recommendedFuel,
                engineDetails = engineDetails,
                cylinderCount = cylinderCount,
                signature = signature,
                nextRevisionDate = nextRevisionDate,
                nextItvDate = nextItvDate,
                firstRegistrationDate = firstRegistrationDate,
                maintenances = emptyList()
            )
        )
    }

    override suspend fun updateVehicle(
        vin: String,
        plate: String,
        brand: String,
        model: String,
        vehicleType: VehicleType,
        year: Long,
        color: String,
        owner: String,
        engineOil: String,
        frontTireSize: String,
        rearTireSize: String,
        frontTirePressureBelow160: Double,
        rearTirePressureBelow160: Double,
        frontTirePressureAbove160: Double,
        rearTirePressureAbove160: Double,
        recommendedFuel: String,
        engineDetails: String,
        cylinderCount: Int,
        frontBulbType: String,
        rearBulbType: String,
        batterySpecs: String,
        coolantType: String,
        signature: String?,
        nextRevisionDate: Long?,
        nextItvDate: Long?,
        firstRegistrationDate: Long?
    ) {
        data.remote.garage.updateVehicle(
            vin = vin,
            vehicle = DVehicle(
                vin = vin,
                plate = plate,
                brand = brand,
                model = model,
                vehicleType = vehicleType,
                year = year.toInt(),
                color = color,
                owner = owner,
                engineOil = engineOil,
                frontTireSize = frontTireSize,
                rearTireSize = rearTireSize,
                frontTirePressureBelow160 = frontTirePressureBelow160,
                rearTirePressureBelow160 = rearTirePressureBelow160,
                frontTirePressureAbove160 = frontTirePressureAbove160,
                rearTirePressureAbove160 = rearTirePressureAbove160,
                recommendedFuel = recommendedFuel,
                engineDetails = engineDetails,
                cylinderCount = cylinderCount,
                frontBulbType = frontBulbType,
                rearBulbType = rearBulbType,
                batterySpecs = batterySpecs,
                coolantType = coolantType,
                signature = signature,
                nextRevisionDate = nextRevisionDate,
                nextItvDate = nextItvDate,
                firstRegistrationDate = firstRegistrationDate,
                maintenances = emptyList()
            )
        ).let { response ->
            if (response is DResponse.Success) {
                data.db.garage.updateVehicle(
                    vin = vin,
                    plate = plate,
                    brand = brand,
                    model = model,
                    vehicleType = vehicleType.code,
                    year = year,
                    color = color,
                    owner = owner,
                    engineOil = engineOil,
                    frontTireSize = frontTireSize,
                    rearTireSize = rearTireSize,
                    frontTirePressureBelow160 = frontTirePressureBelow160,
                    rearTirePressureBelow160 = rearTirePressureBelow160,
                    frontTirePressureAbove160 = frontTirePressureAbove160,
                    rearTirePressureAbove160 = rearTirePressureAbove160,
                    recommendedFuel = recommendedFuel,
                    engineDetails = engineDetails,
                    cylinderCount = cylinderCount,
                    frontBulbType = frontBulbType,
                    rearBulbType = rearBulbType,
                    batterySpecs = batterySpecs,
                    coolantType = coolantType,
                    signature = signature,
                    nextRevisionDate = response.data.nextRevisionDate,
                    nextItvDate = response.data.nextItvDate,
                    firstRegistrationDate = response.data.firstRegistrationDate
                )
            } else {
                FileUtils.log("Error updating vehicle: ${(response as? DResponse.Error)?.error}")
            }
        }
    }

    override suspend fun addVehicleToLocalGarage(vehicle: DVehicle) {
        if (data.db.garage.selectVehicle(vehicle.vin) != null) {
            // El vehículo ya existe localmente, ignoramos silenciosamente
            FileUtils.log("addVehicleToLocalGarage: Vehicle ${vehicle.vin} already exists locally, skipping")
            return
        }
        data.db.garage.insertVehicle(
            vin = vehicle.vin,
            plate = vehicle.plate,
            brand = vehicle.brand,
            model = vehicle.model,
            vehicleType = vehicle.vehicleType.code,
            year = vehicle.year.toLong(),
            color = vehicle.color,
            owner = vehicle.owner,
            engineOil = vehicle.engineOil,
            frontTireSize = vehicle.frontTireSize,
            rearTireSize = vehicle.rearTireSize,
            frontTirePressureBelow160 = vehicle.frontTirePressureBelow160,
            rearTirePressureBelow160 = vehicle.rearTirePressureBelow160,
            frontTirePressureAbove160 = vehicle.frontTirePressureAbove160,
            rearTirePressureAbove160 = vehicle.rearTirePressureAbove160,
            recommendedFuel = vehicle.recommendedFuel,
            engineDetails = vehicle.engineDetails,
            cylinderCount = vehicle.cylinderCount,
            frontBulbType = vehicle.frontBulbType,
            rearBulbType = vehicle.rearBulbType,
            batterySpecs = vehicle.batterySpecs,
            coolantType = vehicle.coolantType,
            signature = vehicle.signature,
            nextRevisionDate = vehicle.nextRevisionDate,
            nextItvDate = vehicle.nextItvDate,
            firstRegistrationDate = vehicle.firstRegistrationDate
        )
    }

    override suspend fun deleteVehicleLocal(vin: String) {
        data.db.garage.deleteVehicle(vin)
    }

    override fun getMaintenances(scope: CoroutineContext): Flow<List<Maintenance>> {
        return data.db.maintenance.observeMaintenances(scope)
    }

    override suspend fun getMaintenancesByVehicleWithSpareParts(vehicleVin: String): List<DMaintenance> {
        return data.db.maintenance.selectMaintenancesByVehicle(vehicleVin)
    }
    //Método que compara una lista de mantenimientos con lo que hay en base de datos y hace limpiado de datos que no estén en el servidor
    //recorre mantenimientos en base de datos y compara con la lista q viene por parámetro, si un mantenimiento no está en esa lista,
    //se borra de base de datos el mantenimiento y su relación con los repuestos, además de los repuestos relacionados
    override suspend fun syncMaintenances(serverMaintenances: List<DMaintenance>) {
        val vehicleVin = serverMaintenances.firstOrNull()?.vehicleVin ?: return
        val localMaintenances = getMaintenancesByVehicleWithSpareParts(vehicleVin)
        val serverIds = serverMaintenances.mapNotNull { it.id }

        // 1. Delete local data that is no longer on server
        localMaintenances.forEach { local ->
            if (local.id != null && !serverIds.contains(local.id)) {
                clearMaintenanceData(local)
            }
        }

        // 2. UPSERT server data to local database
        serverMaintenances.forEach { serverMaintenance ->
            val serverId = serverMaintenance.id ?: return@forEach
            
            // Re-map to local state
            val existing = data.db.maintenance.selectByServerId(serverId)
            
            val localMaintenanceId: Long
            if (existing != null) {
                data.db.maintenance.updateMaintenance(
                    id = existing.id,
                    serverId = serverId,
                    vehicleVin = serverMaintenance.vehicleVin,
                    date = serverMaintenance.date,
                    description = serverMaintenance.description,
                    cost = serverMaintenance.cost,
                    odometer = serverMaintenance.kilometers.toLong(),
                    mechanic = serverMaintenance.mechanic,
                    workshop = serverMaintenance.workshop,
                    signature = serverMaintenance.signature
                )
                localMaintenanceId = existing.id
            } else {
                data.db.maintenance.insertMaintenance(
                    serverId = serverId,
                    vehicleVin = serverMaintenance.vehicleVin,
                    date = serverMaintenance.date,
                    description = serverMaintenance.description,
                    cost = serverMaintenance.cost,
                    odometer = serverMaintenance.kilometers.toLong(),
                    mechanic = serverMaintenance.mechanic,
                    workshop = serverMaintenance.workshop,
                    signature = serverMaintenance.signature
                )
                localMaintenanceId = data.db.maintenance.lastInsertRowId()
            }

            // Sync Spare Parts for this maintenance
            // First, clear existing local relations for this maintenance to ensure we match server state
            data.db.sparePart.deleteMaintenanceSparePartRelation(localMaintenanceId)

            serverMaintenance.spareParts.forEach { sparePart ->
                val sparePartServerId = sparePart.id ?: return@forEach
                
                val existingPart = data.db.sparePart.selectByServerId(sparePartServerId)
                val localSparePartId: Long
                
                if (existingPart != null) {
                    data.db.sparePart.updateSparePart(
                        id = existingPart.id,
                        serverId = sparePartServerId,
                        categoryId = sparePart.categoryId,
                        sparePartId = sparePart.sparePartId,
                        brand = sparePart.brand,
                        model = sparePart.model,
                        additionalInfo = sparePart.additionalInfo,
                        durabilityDays = sparePart.durabilityDays?.toLong(),
                        quantity = sparePart.quantity,
                        unit = sparePart.unit,
                        reference = sparePart.reference,
                        signature = sparePart.signature
                    )
                    localSparePartId = existingPart.id
                } else {
                    data.db.sparePart.insertSparePart(
                        serverId = sparePartServerId,
                        categoryId = sparePart.categoryId,
                        sparePartId = sparePart.sparePartId,
                        brand = sparePart.brand,
                        model = sparePart.model,
                        additionalInfo = sparePart.additionalInfo,
                        durabilityDays = sparePart.durabilityDays?.toLong(),
                        quantity = sparePart.quantity,
                        unit = sparePart.unit,
                        reference = sparePart.reference,
                        signature = sparePart.signature
                    )
                    localSparePartId = data.db.sparePart.lastInsertRowId()
                }

                // Re-establish relation using local IDs
                data.db.sparePart.insertMaintenanceSparePartRelation(
                    maintenanceId = localMaintenanceId,
                    sparePartId = localSparePartId
                )
            }
        }
    }

    private suspend fun clearMaintenanceData(maintenance: DMaintenance) {
        val targetId = maintenance.localId ?: maintenance.id?.let { data.db.maintenance.selectByServerId(it)?.id }
        
        targetId?.let { maintenanceLocalId ->
            // Delete maintenance from local database
            data.db.maintenance.deleteMaintenance(maintenanceLocalId)
            
            // limpiamos también los spare partes relacionados
            maintenance.spareParts.forEach { sparePart ->
                val partLocalId = sparePart.localId ?: sparePart.id?.let { data.db.sparePart.selectByServerId(it)?.id }
                if (partLocalId != null) {
                    data.db.sparePart.deleteSparePartById(partLocalId)
                }
            }
            data.db.sparePart.deleteMaintenanceSparePartRelation(maintenanceLocalId)
        }
    }


    override suspend fun addMaintenance(
        maintenance: DMaintenance
    ): DResponse<DMaintenance> {
        data.remote.maintenance.createMaintenance(
            maintenance = maintenance,
            vehicleVin = maintenance.vehicleVin
        ).let { response ->
            if (response is DResponse.Success) {
                val serverMaintenanceId = response.data.id
                serverMaintenanceId?.let {
                    // Check if exists locally
                    val existing = data.db.maintenance.selectByServerId(serverMaintenanceId)
                    val localMaintenanceId: Long
                    
                    if (existing != null) {
                        data.db.maintenance.updateMaintenance(
                            id = existing.id,
                            serverId = serverMaintenanceId,
                            vehicleVin = response.data.vehicleVin,
                            date = response.data.date,
                            description = response.data.description,
                            cost = response.data.cost,
                            odometer = response.data.kilometers.toLong(),
                            mechanic = response.data.mechanic,
                            workshop = response.data.workshop,
                            signature = response.data.signature
                        )
                        localMaintenanceId = existing.id
                    } else {
                        data.db.maintenance.insertMaintenance(
                            serverId = serverMaintenanceId,
                            vehicleVin = response.data.vehicleVin,
                            date = response.data.date,
                            description = response.data.description,
                            cost = response.data.cost,
                            odometer = response.data.kilometers.toLong(),
                            mechanic = response.data.mechanic,
                            workshop = response.data.workshop,
                            signature = response.data.signature
                        )
                        localMaintenanceId = data.db.maintenance.lastInsertRowId()
                    }

                    // Guardar los spare parts asociados y sus relaciones
                    response.data.spareParts.forEach { sparePart ->
                        val sparePartServerId = sparePart.id ?: return@forEach
                        val existingPart = data.db.sparePart.selectByServerId(sparePartServerId)
                        val localSparePartId: Long
                        
                        if (existingPart != null) {
                            data.db.sparePart.updateSparePart(
                                id = existingPart.id,
                                serverId = sparePartServerId,
                                categoryId = sparePart.categoryId,
                                sparePartId = sparePart.sparePartId,
                                brand = sparePart.brand,
                                model = sparePart.model,
                                additionalInfo = sparePart.additionalInfo,
                                durabilityDays = sparePart.durabilityDays?.toLong(),
                                quantity = sparePart.quantity,
                                unit = sparePart.unit,
                                reference = sparePart.reference,
                                signature = sparePart.signature
                            )
                            localSparePartId = existingPart.id
                        } else {
                            data.db.sparePart.insertSparePart(
                                serverId = sparePartServerId,
                                categoryId = sparePart.categoryId,
                                sparePartId = sparePart.sparePartId,
                                brand = sparePart.brand,
                                model = sparePart.model,
                                additionalInfo = sparePart.additionalInfo,
                                durabilityDays = sparePart.durabilityDays?.toLong(),
                                quantity = sparePart.quantity,
                                unit = sparePart.unit,
                                reference = sparePart.reference,
                                signature = sparePart.signature
                            )
                            localSparePartId = data.db.sparePart.lastInsertRowId()
                        }

                        // Relación
                        data.db.sparePart.deleteMaintenanceSparePartRelation(localMaintenanceId) // Clean first
                        data.db.sparePart.insertMaintenanceSparePartRelation(
                            maintenanceId = localMaintenanceId,
                            sparePartId = localSparePartId
                        )
                    }
                } ?: run {
                    return DResponse.Error("Maintenance ID is null")
                }

                try {
                    data.firebase.analytics.logEvent("maintenance_added", maintenance.vehicleVin)
                } catch (e: Exception) {
                    e.printStackTrace()
                }
            }
            return response
        }
    }

    override suspend fun updateMaintenance(maintenance: DMaintenance): DResponse<DMaintenance> {
        val serverId = maintenance.id ?: return DResponse.Error("Maintenance ID is null")
        val response = data.remote.maintenance.updateMaintenance(serverId, maintenance)
        if (response is DResponse.Success) {
            val localId = maintenance.localId ?: data.db.maintenance.selectByServerId(serverId)?.id
            
            if (localId != null) {
                data.db.maintenance.updateMaintenance(
                    id = localId,
                    serverId = serverId,
                    vehicleVin = maintenance.vehicleVin,
                    date = maintenance.date,
                    description = maintenance.description,
                    cost = maintenance.cost,
                    odometer = maintenance.kilometers.toLong(),
                    mechanic = maintenance.mechanic,
                    workshop = maintenance.workshop,
                    signature = maintenance.signature
                )
            }
            return DResponse.Success(maintenance)
        } else {
            return response
        }
    }

    override suspend fun deleteMaintenance(maintenanceId: Long, vehicleVin: String): DResponse<Unit> {
        // Validate that the maintenance belongs to the user's vehicle
        val maintenance = data.db.maintenance.selectMaintenancesByVehicle(vehicleVin)
            .find { it.id == maintenanceId }
            ?: return DResponse.Error("Maintenance not found")
        
        // Validate that it's not signed
        if (maintenance.isSigned) {
            return DResponse.Error("Cannot delete a signed maintenance")
        }
        
        // Call the DELETE endpoint
        val response = data.remote.maintenance.deleteMaintenance(maintenanceId)
        
        // If the API call was successful, delete from local database
        when (response) {
            is DResponse.Success, is DResponse.Complete -> {
                try {
                    clearMaintenanceData(maintenance)
                    FileUtils.log("Maintenance $maintenanceId deleted successfully from server and local DB")
                } catch (e: Exception) {
                    FileUtils.log("Error deleting maintenance from local DB: ${e.message}")
                }
            }
            is DResponse.Error -> {
                FileUtils.log("Error deleting maintenance from server: ${response.error}")
            }
        }
        
        return response
    }

    override suspend fun signMaintenance(maintenanceId: Long, vehicleVin: String): DResponse<DMaintenance> {
        // 1. Get the maintenance to sign
        // 1. Get the maintenance to sign
        val maintenance = data.db.maintenance.selectMaintenancesByVehicle(vehicleVin)
            .find { it.id == maintenanceId }
            ?: return DResponse.Error("Maintenance not found")

        // 2. Get all maintenances for the vehicle to find the previous one
        val allMaintenances = data.db.maintenance.selectMaintenancesByVehicle(vehicleVin)
            .sortedBy { it.kilometers } // Assuming order by kilometers/date

        val index = allMaintenances.indexOfFirst { it.id == maintenanceId }
        if (index == -1) return DResponse.Error("Maintenance not found in vehicle list")

        // 3. Determine previous signature
        val previousSignature = if (index == 0) {
            // First maintenance, use vehicle signature
            val vehicle = data.db.garage.selectVehicle(vehicleVin)
                ?: return DResponse.Error("Vehicle not found")
            link.erpango.quip.domain.services.SignatureService.signVehicle(vehicle)
        } else {
            // Use previous maintenance signature
            val previousMaintenance = allMaintenances[index - 1]
            previousMaintenance.signature ?: return DResponse.Error("Previous maintenance is not signed")
        }

        // 4. Calculate new signature
        val newSignature = link.erpango.quip.domain.services.SignatureService.signMaintenance(maintenance, previousSignature)

        // 5. Update maintenance with new signature
        val signedMaintenance = maintenance.copy(signature = newSignature)
        return updateMaintenance(signedMaintenance)
    }

    override suspend fun signAllMaintenances(vehicleVin: String): DResponse<List<DMaintenance>> {
        val allMaintenances = data.db.maintenance.selectMaintenancesByVehicle(vehicleVin)
            .sortedBy { it.kilometers }

        val vehicle = data.db.garage.selectVehicle(vehicleVin)
            ?: return DResponse.Error("Vehicle not found")
        val vehicleSignature = link.erpango.quip.domain.services.SignatureService.signVehicle(vehicle)

        var previousSignature = vehicleSignature
        val signedMaintenances = mutableListOf<DMaintenance>()

        for (maintenance in allMaintenances) {
            if (!maintenance.isSigned) {
                val newSignature = link.erpango.quip.domain.services.SignatureService.signMaintenance(maintenance, previousSignature)
                val signedMaintenance = maintenance.copy(signature = newSignature)
                
                val response = updateMaintenance(signedMaintenance)
                if (response is DResponse.Success) {
                    previousSignature = newSignature
                    signedMaintenances.add(signedMaintenance)
                } else {
                    return DResponse.Error("Failed to sign maintenance ${maintenance.id}")
                }
            } else {
                previousSignature = maintenance.signature!!
            }
        }
        return DResponse.Success(signedMaintenances)
    }

    override suspend fun fetchMyGarage(): DResponse<List<DVehicle>> {
        return data.remote.garage.getMyGarage()
    }

    override fun getFirebaseAuthAccesor() = data.firebase.auth
    override fun getUserFlow(scope: CoroutineContext) = data.db.auth.observeAuth(scope)
    override fun observeUserFlow(scope: CoroutineContext) = data.db.user.observeUser(scope)
    override suspend fun getLoggedUser() = data.db.user.selectCurrentUser()

    override suspend fun fetchMaintenancesByVehicle(vehicleVin: String): DResponse<List<DMaintenance>> {
        val response = data.remote.maintenance.getMaintenancesByVehicle(vehicleVin)

        if (response is DResponse.Success) {
            syncMaintenances(response.data)
        }

        return response
    }

    override suspend fun getAppVersion(): String? {
        return data.db.auth.getAppVersionCode()
    }

    override suspend fun getLatestSparePartByCategoryAndType(
        vehicleVin: String,
        categoryId: String,
        sparePartId: String
    ): DSparePart? {
        return data.db.sparePart.selectLatestSparePartByCategoryAndType(
            vehicleVin = vehicleVin,
            categoryId = categoryId,
            sparePartId = sparePartId
        )
    }
    
    override suspend fun saveTheme(themeName: String) {
        data.db.commonValues.insertValue(
            "app_theme",
            themeName,
            DatetimeUtils.currentTimeMillis()
        )
    }

    override suspend fun getSavedTheme(): String? {
        return data.db.commonValues.selectValue("app_theme")
    }
    
    // === Funciones para autocompletado en formularios ===
    
    override suspend fun getDistinctMechanics(): List<String> {
        return data.db.maintenance.selectDistinctMechanics()
    }
    
    override suspend fun getDistinctWorkshops(): List<String> {
        return data.db.maintenance.selectDistinctWorkshops()
    }
    
    override suspend fun getDistinctSparePartBrands(): List<String> {
        return data.db.sparePart.selectDistinctBrands()
    }
    
    override suspend fun getDistinctSparePartModels(): List<String> {
        return data.db.sparePart.selectDistinctModels()
    }
    
    override suspend fun getDistinctDurabilityDays(): List<Int> {
        return data.db.sparePart.selectDistinctDurabilityDays()
    }
    
    override suspend fun getDistinctOwners(): List<String> {
        return data.db.garage.selectDistinctOwners()
    }

    override fun getDistinctMechanicsFlow(scope: CoroutineContext): Flow<List<String>> {
        return data.db.maintenance.observeDistinctMechanics(scope)
    }

    override fun getDistinctWorkshopsFlow(scope: CoroutineContext): Flow<List<String>> {
        return data.db.maintenance.observeDistinctWorkshops(scope)
    }

    override fun getDistinctSparePartBrandsFlow(vehicleType: String?, scope: CoroutineContext): Flow<List<String>> {
        return data.db.sparePart.observeDistinctBrands(vehicleType, scope)
    }

    override fun getDistinctSparePartModelsFlow(vehicleType: String?, scope: CoroutineContext): Flow<List<String>> {
        return data.db.sparePart.observeDistinctModels(vehicleType, scope)
    }

    override fun getDistinctDurabilityDaysFlow(scope: CoroutineContext): Flow<List<Int>> {
        return data.db.sparePart.observeDistinctDurabilityDays(scope)
    }

    // Documents
    override suspend fun getDocuments(vin: String): List<DVehicleDocument> {
        return data.db.vehicleDocuments.selectByVin(vin)
    }

    override suspend fun getVehicleDocumentsMetadata(vin: String): DResponse<List<DVehicleDocument>> {
        val response = data.remote.garage.getDocuments(vin)
        if (response is DResponse.Success) {
            reconcileDocuments(vin, response.data)
        }
        return response
    }

    private suspend fun reconcileDocuments(vin: String, serverDocs: List<DVehicleDocument>) {
        val localDocs = data.db.vehicleDocuments.selectByVin(vin)
        
        serverDocs.forEach { serverDoc ->
            val existing = data.db.vehicleDocuments.selectByServerId(serverDoc.id)
            if (existing == null) {
                // Check if there's a local-only doc with the same name (just uploaded, but sync arrived first or something)
                val localOnly = localDocs.find { it.id == 0L && it.name == serverDoc.name }
                if (localOnly != null && localOnly.localId != null) {
                    // Update the local-only doc with server ID
                    val docToSave = serverDoc.copy(localId = localOnly.localId, filePath = localOnly.filePath)
                    data.db.vehicleDocuments.insert(docToSave)
                } else {
                    // New document from server
                    data.db.vehicleDocuments.insert(serverDoc)
                }
            } else {
                // Update existing
                val docToSave = serverDoc.copy(
                    localId = existing.localId,
                    filePath = if (existing.filePath.isNotEmpty()) existing.filePath else serverDoc.filePath
                )
                data.db.vehicleDocuments.insert(docToSave)
            }
        }
    }

    override suspend fun uploadDocument(document: DVehicleDocument): DResponse<DVehicleDocument> {
        // Save locally first with no server ID
        val localId = data.db.vehicleDocuments.insert(document.copy(id = 0L))
        val localDoc = document.copy(localId = localId, id = 0L)

        // Use the base64 content already on the document (from file picker), or read from file
        val base64Content = document.fileContentBase64?.takeIf { it.isNotEmpty() }
            ?: FileUtils.readFileToBase64(document.filePath)
        if (base64Content.isEmpty()) {
            return DResponse.Error("Failed to read file content")
        }

        // Upload to API
        data.remote.garage.uploadDocument(document.vehicleVin, document, base64Content).let { response ->
            if (response is DResponse.Success) {
                // Save to local DB with the ID from server and KEEP local path
                val serverDoc = response.data
                val docToSave = serverDoc.copy(localId = localId, filePath = document.filePath)
                data.db.vehicleDocuments.insert(docToSave)
                return DResponse.Success(docToSave)
            } else {
                val error = response as? DResponse.Error
                return DResponse.Error(error?.error ?: "Upload failed")
            }
        }
    }

    override suspend fun syncDocuments(vin: String): DResponse<Unit> {
        data.remote.garage.getDocuments(vin).let { response ->
            if (response is DResponse.Success) {
                // Useize centralized reconciliation or manual sync
                val serverDocs = response.data
                val localDocs = data.db.vehicleDocuments.selectByVin(vin)
                
            serverDocs.forEach { serverDoc ->
                val existing = localDocs.find { it.id == serverDoc.id }
                // Ignore server URL, use existing local path or empty
                var path = "" 
                
                if (existing != null && existing.filePath.isNotEmpty()) {
                    path = existing.filePath
                }
                
                // If we have base64 in the list response (unlikely for light serializer)
                if (serverDoc.fileBase64 != null && serverDoc.fileBase64.isNotEmpty()) {
                    path = FileUtils.saveBase64ToFile(serverDoc.fileBase64, serverDoc.name)
                }

                data.db.vehicleDocuments.insert(serverDoc.copy(
                    localId = existing?.localId,
                    filePath = path
                ))
            }
            
            // Delete local docs that are not on server
            val serverIds = serverDocs.map { it.id }
            localDocs.forEach { localDoc ->
                if (localDoc.id != 0L && !serverIds.contains(localDoc.id)) {
                    localDoc.localId?.let { data.db.vehicleDocuments.deleteById(it) }
                }
            }
            
            return DResponse.Success(Unit)
        } else {
            val error = response as? DResponse.Error
            return DResponse.Error(error?.error ?: "Sync failed")
        }
    }
    }


    override suspend fun downloadDocument(documentId: Long): DResponse<DVehicleDocument> {
        val localDoc = data.db.vehicleDocuments.selectByServerId(documentId)
            ?: return DResponse.Error("Document not found locally")
            
        return data.remote.garage.downloadDocument(documentId).let { response ->
            if (response is DResponse.Success) {
                val serverDoc = response.data
                if (serverDoc.fileBase64 != null) {
                    val path = FileUtils.saveBase64ToFile(serverDoc.fileBase64, serverDoc.name)
                    data.db.vehicleDocuments.insert(serverDoc.copy(
                        localId = localDoc.localId,
                        filePath = path
                    ))
                    DResponse.Success(serverDoc.copy(filePath = path))
                } else {
                    DResponse.Error("No content received")
                }
            } else {
                response
            }
        }
    }

    override suspend fun deleteDocument(document: DVehicleDocument): DResponse<Unit> {
        val serverId = document.id
        if (serverId == 0L) {
             // Local only, just delete locally
             document.localId?.let { data.db.vehicleDocuments.deleteById(it) }
             return DResponse.Success(Unit)
        }
        
        data.remote.garage.deleteDocument(serverId).let { response ->
             if (response is DResponse.Success || response is DResponse.Complete) {
                 document.localId?.let { data.db.vehicleDocuments.deleteById(it) }
                 return DResponse.Success(Unit)
             }
             val error = response as? DResponse.Error
             return DResponse.Error(error?.error ?: "Delete failed")
        }
    }

    override suspend fun renameDocument(document: DVehicleDocument, newName: String): DResponse<DVehicleDocument> {
        val serverId = document.id
        if (serverId == 0L) {
             // Local only
             val docToSave = document.copy(name = newName, updatedAt = DatetimeUtils.currentTimeMillis().toString())
             data.db.vehicleDocuments.insert(docToSave)
             return DResponse.Success(docToSave)
        }
        
        val response = data.remote.garage.renameDocument(serverId, newName)
        if (response is DResponse.Success) {
            val serverDoc = response.data
            
            // Preserve local fields
            val docToSave = serverDoc.copy(
                localId = document.localId,
                filePath = document.filePath,
                fileBase64 = if (serverDoc.fileBase64.isNullOrEmpty()) document.fileBase64 else serverDoc.fileBase64
            )
            
            FileUtils.log("renameDocument: Updated name to ${docToSave.name}, updating local DB.")
            // Update local DB
            data.db.vehicleDocuments.insert(docToSave)
            return DResponse.Success(docToSave)
        }
        return response
    }

    override suspend fun getDocument(documentId: Long): DResponse<DVehicleDocument> {
        return data.remote.garage.getDocument(documentId)
    }

    override suspend fun updateLocalDocumentPath(document: DVehicleDocument, newPath: String) {
        data.db.vehicleDocuments.insert(document.copy(filePath = newPath))
    }

    // Spare Part Documents
    override suspend fun getSparePartDocuments(sparePartServerId: Long): List<DSparePartDocument> {
        val localSparePartId = data.db.sparePart.selectByServerId(sparePartServerId)?.id ?: return emptyList()
        return data.db.sparePartDocuments.selectBySparePartId(localSparePartId)
    }

    override suspend fun getSparePartDocumentsMetadata(sparePartServerId: Long): DResponse<List<DSparePartDocument>> {
        val localSparePartId = data.db.sparePart.selectByServerId(sparePartServerId)?.id
            ?: return DResponse.Error("Local Spare Part not found")
            
        val response = data.remote.garage.getSparePartDocuments(sparePartServerId)
        if (response is DResponse.Success) {
            reconcileSparePartDocuments(localSparePartId, response.data)
        }
        return response
    }

    private suspend fun reconcileSparePartDocuments(sparePartId: Long, serverDocs: List<DSparePartDocument>) {
        val localDocs = data.db.sparePartDocuments.selectBySparePartId(sparePartId)
        
        serverDocs.forEach { serverDoc ->
            val existing = data.db.sparePartDocuments.selectByServerId(serverDoc.id)
            if (existing == null) {
                // Check if there's a local-only doc with the same name (just uploaded, but sync arrived first or something)
                val localOnly = localDocs.find { it.id == 0L && it.name == serverDoc.name }
                if (localOnly != null && localOnly.localId != null) {
                    // Update the local-only doc with server ID
                    val docToSave = serverDoc.copy(
                        localId = localOnly.localId, 
                        localSparePartId = sparePartId,
                        filePath = localOnly.filePath
                    )
                    data.db.sparePartDocuments.insert(docToSave)
                } else {
                    // New document from server
                    data.db.sparePartDocuments.insert(serverDoc.copy(localSparePartId = sparePartId))
                }
            } else {
                // Update existing
                val docToSave = serverDoc.copy(
                    localId = existing.localId,
                    localSparePartId = sparePartId,
                    filePath = if (existing.filePath.isNotEmpty()) existing.filePath else serverDoc.filePath
                )
                data.db.sparePartDocuments.insert(docToSave)
            }
        }
    }

    override suspend fun uploadSparePartDocument(document: DSparePartDocument): DResponse<DSparePartDocument> {
        // Find local ID of spare part
        val localSparePartId = document.localSparePartId ?: data.db.sparePart.selectByServerId(document.sparePartId)?.id
            ?: return DResponse.Error("Local Spare Part not found")
            
        // Save locally first with no server ID
        val localId = data.db.sparePartDocuments.insert(document.copy(id = 0L, localSparePartId = localSparePartId))
        val localDoc = document.copy(localId = localId, id = 0L, localSparePartId = localSparePartId)

        // Use the base64 content already on the document (from file picker), or read from file
        val base64Content = document.fileContentBase64?.takeIf { it.isNotEmpty() }
            ?: FileUtils.readFileToBase64(document.filePath)
        if (base64Content.isEmpty()) {
            return DResponse.Error("Failed to read file content")
        }

        // Upload to API
        data.remote.garage.uploadSparePartDocument(localDoc, base64Content).let { response ->
            if (response is DResponse.Success) {
                // Save to local DB with the ID from server and KEEP local path
                val serverDoc = response.data
                val docToSave = serverDoc.copy(localId = localId, localSparePartId = localSparePartId, filePath = document.filePath)
                data.db.sparePartDocuments.insert(docToSave)
                return DResponse.Success(docToSave)
            } else {
                 val error = response as? DResponse.Error
                 return DResponse.Error(error?.error ?: "Upload failed")
            }
        }
    }

    override suspend fun syncSparePartDocuments(sparePartServerId: Long): DResponse<Unit> {
        // Resolve localSparePartId
        val localSparePartId = data.db.sparePart.selectByServerId(sparePartServerId)?.id
            ?: return DResponse.Error("Local Spare Part not found")
            
        return data.remote.garage.getSparePartDocuments(sparePartServerId).let { response ->
            if (response is DResponse.Success) {
                val serverDocs = response.data
                val localDocs = data.db.sparePartDocuments.selectBySparePartId(localSparePartId)
                
            serverDocs.forEach { serverDoc ->
                val existing = localDocs.find { it.id == serverDoc.id }
                // Ignore server URL, use existing local path or empty
                var path = ""
                
                if (existing != null && existing.filePath.isNotEmpty()) {
                    path = existing.filePath
                }
                
                // If we have base64 in the list response (unlikely for light serializer)
                if (serverDoc.fileBase64 != null && serverDoc.fileBase64.isNotEmpty()) {
                    path = FileUtils.saveBase64ToFile(serverDoc.fileBase64, serverDoc.name)
                }

                data.db.sparePartDocuments.insert(serverDoc.copy(
                    localId = existing?.localId,
                    localSparePartId = localSparePartId,
                    filePath = path
                ))
            }
            
            // Delete local docs that are not on server
            val serverIds = serverDocs.map { it.id }
            localDocs.forEach { local ->
                if (local.id != 0L && !serverIds.contains(local.id)) {
                    local.localId?.let { data.db.sparePartDocuments.deleteById(it) }
                }
            }
            
            DResponse.Success(Unit)
        } else {
            val error = response as? DResponse.Error
            DResponse.Error(error?.error ?: "Sync failed")
        }
    }
    }


    override suspend fun downloadSparePartDocument(documentId: Long): DResponse<DSparePartDocument> {
        val localDoc = data.db.sparePartDocuments.selectByServerId(documentId)
            ?: return DResponse.Error("Document not found locally")

        return data.remote.garage.downloadSparePartDocument(documentId).let { response ->
            if (response is DResponse.Success) {
                val serverDoc = response.data
                if (serverDoc.fileBase64 != null) {
                    val path = FileUtils.saveBase64ToFile(serverDoc.fileBase64, serverDoc.name)
                    // We need to resolve localSparePartId again because serverDoc might not have it populated for local DB
                    // actually serverDoc comes from Detail endpoint, has sparePartId (server), we need localSparePartId
                    val localSparePartId = data.db.sparePart.selectByServerId(serverDoc.sparePartId)?.id
                    
                    data.db.sparePartDocuments.insert(serverDoc.copy(
                        localId = localDoc.localId,
                        localSparePartId = localSparePartId,
                        filePath = path
                    ))
                    DResponse.Success(serverDoc.copy(filePath = path))
                } else {
                    DResponse.Error("No content received")
                }
            } else {
                response
            }
        }
    }

    override suspend fun deleteSparePartDocument(document: DSparePartDocument): DResponse<Unit> {
        val serverId = document.id
        if (serverId == 0L) {
             // Local only
             document.localId?.let { data.db.sparePartDocuments.deleteById(it) }
             return DResponse.Success(Unit)
        }
        
        data.remote.garage.deleteSparePartDocument(serverId).let { response ->
            if (response is DResponse.Success || response is DResponse.Complete) {
                document.localId?.let { data.db.sparePartDocuments.deleteById(it) }
                return DResponse.Success(Unit)
            }
            val error = response as? DResponse.Error
            return DResponse.Error(error?.error ?: "Delete failed")
        }
    }

    override suspend fun renameSparePartDocument(document: DSparePartDocument, newName: String): DResponse<DSparePartDocument> {
        val serverId = document.id
        if (serverId == 0L) {
             // Local only
             val docToSave = document.copy(name = newName, updatedAt = DatetimeUtils.currentTimeMillis().toString())
             data.db.sparePartDocuments.insert(docToSave)
             return DResponse.Success(docToSave)
        }
        
        val response = data.remote.garage.renameSparePartDocument(serverId, newName)
        if (response is DResponse.Success) {
            val serverDoc = response.data
            val docToSave = serverDoc.copy(
                localId = document.localId,
                localSparePartId = document.localSparePartId,
                filePath = document.filePath
            )
            data.db.sparePartDocuments.insert(docToSave)
            return DResponse.Success(docToSave)
        }
        return response
    }

    override suspend fun getSparePartDocument(documentId: Long): DResponse<DSparePartDocument> {
        val doc = data.db.sparePartDocuments.selectById(documentId)
        return if (doc != null) DResponse.Success(doc) else DResponse.Error("Document not found")
    }

    override suspend fun updateLocalSparePartDocumentPath(document: DSparePartDocument, newPath: String) {
        val localSparePartId = document.localSparePartId ?: data.db.sparePart.selectByServerId(document.sparePartId)?.id

        data.db.sparePartDocuments.insert(document.copy(
            filePath = newPath,
            localSparePartId = localSparePartId
        ))
    }

    override suspend fun getCatalogBrands(): List<String> {
        return data.db.catalog.selectAllBrandNames()
    }

    override suspend fun getCatalogModelsByBrand(brandName: String): List<String> {
        return data.db.catalog.selectModelNamesByBrandName(brandName)
    }

    // Vehicle Transfer - QR/OTP
    
    override suspend fun initiateQrTransfer(vin: String, targetUserId: Int, expiryHours: Int): DResponse<TransferQRResponse> {
        return try {
            val response = data.remote.garage.initiateQrTransfer(vin, targetUserId, expiryHours)
            when (response) {
                is DResponse.Success -> DResponse.Success(response.data)
                is DResponse.Error -> response
                else -> DResponse.Error("Unexpected response type", null)
            }
        } catch (e: Exception) {
            DResponse.Error(e.message ?: "Error desconocido", e)
        }
    }

    override suspend fun getActiveQrTransfer(vin: String): DResponse<TransferQRResponse?> {
        return try {
            data.remote.garage.getActiveQrTransfer(vin)
        } catch (e: Exception) {
            DResponse.Error(e.message ?: "Error desconocido", e)
        }
    }

    override suspend fun getTransferRecipient(userId: Int): DResponse<link.erpango.quip.domain.entities.TransferRecipient> {
        return try {
            data.remote.garage.getTransferRecipient(userId)
        } catch (e: Exception) {
            DResponse.Error(e.message ?: "Error desconocido", e)
        }
    }

    override suspend fun acceptTransfer(token: String): DResponse<AcceptTransferResponse> {
        return try {
            val response = data.remote.garage.acceptTransfer(token)
            when (response) {
                is DResponse.Success -> DResponse.Success(response.data)
                is DResponse.Error -> response
                else -> DResponse.Error("Unexpected response type", null)
            }
        } catch (e: Exception) {
            DResponse.Error(e.message ?: "Error desconocido", e)
        }
    }

    override suspend fun cancelTransfer(token: String): DResponse<CancelTransferResponse> {
        return try {
            val response = data.remote.garage.cancelTransfer(token)
            when (response) {
                is DResponse.Success -> DResponse.Success(response.data)
                is DResponse.Error -> response
                else -> DResponse.Error("Unexpected response type", null)
            }
        } catch (e: Exception) {
            DResponse.Error(e.message ?: "Error desconocido", e)
        }
    }

    override suspend fun getPendingTransfers(): DResponse<List<PendingTransferItem>> {
        return try {
            val response = data.remote.garage.getPendingTransfers()
            when (response) {
                is DResponse.Success -> DResponse.Success(response.data)
                is DResponse.Error -> response
                else -> DResponse.Error("Unexpected response type", null)
            }
        } catch (e: Exception) {
            DResponse.Error(e.message ?: "Error desconocido", e)
        }
    }

    override suspend fun checkTransferStatus(token: String): DResponse<link.erpango.quip.domain.entities.TransferStatusResponse> {
        return try {
            val response = data.remote.garage.checkTransferStatus(token)
            when (response) {
                is DResponse.Success -> DResponse.Success(response.data)
                is DResponse.Error -> response
                else -> DResponse.Error("Unexpected response type", null)
            }
        } catch (e: Exception) {
            DResponse.Error(e.message ?: "Error desconocido", e)
        }
    }

    // Vehicle Transfer - Direct
    
    override suspend fun initiateDirectTransfer(vin: String, targetUserId: Int, transferIsPrimary: Boolean): DResponse<AcceptTransferResponse> {
        return try {
            val response = data.remote.garage.initiateDirectTransfer(vin, targetUserId, transferIsPrimary)
            when (response) {
                is DResponse.Success -> DResponse.Success(response.data)
                is DResponse.Error -> DResponse.Error(response.error, response.throwable)
                else -> DResponse.Error("Unexpected response type", null)
            }
        } catch (e: Exception) {
            DResponse.Error(e.message ?: "Error desconocido", e)
        }
    }
}
